/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import https from 'https';

const uuid = require('uuid/v1');

import getConfig from '../config';
import { getLogger } from '../utils/SearchLogger';
import PerformanceUtils from '../utils/PerformanceUtils';
import SearchAnalytics from '../analytics/SearchAnalytics';

export default class BotService {
	private static instance: BotService;
	searchHostContext: HostContext;
	logger: Logger;
	constructor(searchHostContext: HostContext) {
		if (!BotService.instance) {
			this.searchHostContext = searchHostContext;
			this.logger = getLogger();
			BotService.instance = this;
		}
		return BotService.instance;
	}

	/**
	 * [queries a bot about the query from searchbar]
	 * @param  {HostContext} searchHostContext
	 * @param  {string} query
	 * @returns {[BotQueryResult]} botResults
	 */
	async query(query: string, searchConfig?: any): Promise<BotQueryResult | Error> {
		const startTime = Date.now();
		try {
			const env = this.searchHostContext.environment;
			const botConfig: any = searchConfig?.bot ?? {};
			const hostname = getConfig(env, 'SEARCH_SERVER');
			const path = '/v1/bot/query';
			const curRequestId = uuid();
			SearchAnalytics.requestId = curRequestId;

			const reqOptions = {
				hostname,
				path,
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					Accept: 'application/json',
					'x-api-key': this.searchHostContext.hostInfo.apiKey,
					authorization: `Bearer ${this.searchHostContext.imsInfo.accessToken}`,
					'x-product': `${getConfig(env, 'APP_NAME')}/${getConfig(env, 'APP_VERSION')}`,
					'x-product-location': `${getConfig(env, 'APP_NAME')}.UniversalSearchService`,
					'x-request-id': curRequestId,
				},
			};

			const payload = {
				query,
				language: this.searchHostContext.locale,
				region: 'us',
				config: { ...botConfig },
			};

			return new Promise((resolve) => {
				const postData = JSON.stringify(payload);
				const startTime = Date.now();
				const request = https.request(reqOptions, (res: any) => {
					// if (res.headers && typeof res.headers['x-request-id'] !== 'undefined') {
					// 	SearchAnalytics.contextGuid = res.headers['x-request-id'];
					// }

					if (res.statusCode >= 300) {
						// On error, it should show the error status code
						const serverData = {
							serverResult: {
								url: `https://${hostname}${path}`,
								status: res.statusCode,
								message: res.statusMessage,
								requestId: curRequestId,
								requestBody: payload,
								duration: Date.now() - startTime,
							},
							hasServerError: true,
						};
						this.logger.error('Bot Service: query', `statusCode error ${res.statusCode}`, {
							server: serverData,
							performanceTiming: PerformanceUtils.getPerformanceTimingByName(res.url),
						});
						resolve(new Error(serverData.serverResult.message));
					} else {
						let result = '';
						// set the encoding to UTF8 to prevent decoding issues on certain browsers.
						res.setEncoding('utf8');
						res.on('data', (chunk) => (result += chunk));
						res.on('end', () => {
							// Success!
							const serverData = {
								serverResult: {
									url: `https://${hostname}${path}`,
									status: res.statusCode,
									message: res.statusMessage,
									requestId: curRequestId,
									requestBody: payload,
									duration: Date.now() - startTime,
								},
								hasServerError: false,
							};

							try {
								const parsedResponseData = JSON.parse(result);

								if (parsedResponseData.errors.length) {
									const botError = parsedResponseData.errors[0];
									serverData.serverResult = {
										...serverData.serverResult,
										message: botError.message || 'Error from bots',
										status: botError.status,
									};
									serverData.hasServerError = true;
									this.logger.error('Bot Service: query', 'error from bot', {
										server: serverData,
										performanceTiming: PerformanceUtils.getPerformanceTimingByName(res.url),
									});
									resolve(new Error(serverData.serverResult.message));
								}

								this.logger.info('Bot Service: query', `${path} endpoint response`, {
									server: serverData,
									performanceTiming: PerformanceUtils.getPerformanceTimingByName(res.url),
								});
								resolve(parsedResponseData);
							} catch (err: any) {
								this.logger.error('Bot Service: query', err.message, {
									server: serverData,
									performanceTiming: PerformanceUtils.getPerformanceTimingByName(res.url),
								});
								resolve(new Error(serverData.serverResult.message));
							}
						});
					}
				});

				request.on('error', (err) => {
					// on error, display the search error message
					const serverData = {
						serverResult: {
							url: `https://${hostname}${path}`,
							status: 408,
							message: err.message,
							requestId: curRequestId,
							requestBody: payload,
							duration: Date.now() - startTime,
						},
						hasServerError: false,
					};
					this.logger.error('Bot Service : query', 'request error', {
						server: serverData,
					});
					resolve(new Error(serverData.serverResult.message));
				});
				request.write(postData);
				request.end();
			});
		} catch (err: any) {
			const loggerData = {
				message: err.message,
				duration: Date.now() - startTime,
			};
			this.logger.error('Bot Service: query', 'Problem with Bot query request', loggerData);
			const error = new Error(`query error : ${err.message}`);
			return Promise.reject(error);
		}
	}
}
